import {
  compareSearchTextMatchSortMeta,
  getSearchTextMatchSortMeta,
  isSearchKeywordEmpty,
  SEARCH_TEXT_MATCH_PRIORITY,
  type SearchTextMatchPriorityOptions,
  type SearchTextMatchSortMeta,
} from '@kc-console/core';

/**
 * 필터·정렬(`getPrimarySubtextsSearchMatchSortMeta`)과 동일한 기준으로, 하이라이트를 붙일 필드.
 */
export type PrimarySubtextsSearchHighlightTarget =
  | { kind: 'none' }
  | { kind: 'primary' }
  | { index: number; kind: 'subText' };

type PrimarySubtextsMatchCompute = {
  highlightTarget: PrimarySubtextsSearchHighlightTarget;
  meta: SearchTextMatchSortMeta;
};

const computePrimarySubtextsMatch = (
  primaryText: string,
  subTexts: readonly unknown[] | undefined,
  searchKeyword: string,
  options?: SearchTextMatchPriorityOptions,
): PrimarySubtextsMatchCompute => {
  const primaryMeta = getSearchTextMatchSortMeta(primaryText, searchKeyword, options);
  if (Number.isFinite(primaryMeta.tier)) {
    return { meta: primaryMeta, highlightTarget: { kind: 'primary' } };
  }

  const noMatch: SearchTextMatchSortMeta = {
    tier: SEARCH_TEXT_MATCH_PRIORITY.NO_MATCH,
    matchIndex: Number.POSITIVE_INFINITY,
  };

  let bestSecondary = noMatch;
  let bestIndex = -1;
  const subs = subTexts ?? [];

  for (let i = 0; i < subs.length; i++) {
    const subStr = String(subs[i] ?? '');
    const subMeta = getSearchTextMatchSortMeta(subStr, searchKeyword, options);
    if (!Number.isFinite(subMeta.tier)) continue;

    const mapped: SearchTextMatchSortMeta = {
      tier: mapSecondaryFieldMatchScore(subMeta.tier),
      matchIndex: subMeta.matchIndex,
    };
    if (compareSearchTextMatchSortMeta(mapped, bestSecondary) < 0) {
      bestSecondary = mapped;
      bestIndex = i;
    }
  }

  if (bestIndex === -1) {
    return { meta: noMatch, highlightTarget: { kind: 'none' } };
  }
  return { meta: bestSecondary, highlightTarget: { kind: 'subText', index: bestIndex } };
};

/**
 * 보조 필드(subTexts 등) 매칭 가중치. GNB 메뉴 검색의 category 단계와 동일하게 exact/startsWith → 3, includes → 4.
 */
export const mapSecondaryFieldMatchScore = (priority: number): number => {
  if (!Number.isFinite(priority)) return SEARCH_TEXT_MATCH_PRIORITY.NO_MATCH;
  return priority === SEARCH_TEXT_MATCH_PRIORITY.INCLUDES ? 4 : 3;
};

/**
 * 검색 정렬·하이라이트 대상 필터와 같은 규칙으로, 하이라이트를 적용할 필드를 반환한다.
 */
export const getPrimarySubtextsSearchHighlightTarget = (
  primaryText: string,
  subTexts: readonly unknown[] | undefined,
  searchKeyword: string,
  options?: SearchTextMatchPriorityOptions,
): PrimarySubtextsSearchHighlightTarget => {
  if (isSearchKeywordEmpty(searchKeyword, options)) return { kind: 'none' };
  return computePrimarySubtextsMatch(primaryText, subTexts, searchKeyword, options).highlightTarget;
};

/**
 * 주 필드(`primaryText`)를 0·1·2단계로 매칭하고, 실패 시 `subTexts` 각각을 보조 필드(3·4단계)로 매칭한다.
 * 동일 tier 내에서는 정규화 문자열 기준 `indexOf`가 더 앞인 보조 필드를 택한다.
 */
export const getPrimarySubtextsSearchMatchSortMeta = (
  primaryText: string,
  subTexts: readonly unknown[] | undefined,
  searchKeyword: string,
  options?: SearchTextMatchPriorityOptions,
): SearchTextMatchSortMeta =>
  computePrimarySubtextsMatch(primaryText, subTexts, searchKeyword, options).meta;

/**
 * 주 필드(`primaryText`)를 0·1·2단계로 매칭하고, 실패 시 `subTexts` 각각을 보조 필드(3·4단계)로 매칭한다.
 */
export const getPrimarySubtextsSearchMatchScore = (
  primaryText: string,
  subTexts: readonly unknown[] | undefined,
  searchKeyword: string,
  options?: SearchTextMatchPriorityOptions,
): number =>
  getPrimarySubtextsSearchMatchSortMeta(primaryText, subTexts, searchKeyword, options).tier;

/**
 * 정렬 tie-break용 라벨 (매칭 계산에는 사용하지 않음).
 */
export const getPrimarySubtextsSortLabel = (
  primaryText: string,
  subTexts: readonly unknown[] | undefined,
): string => {
  const subs = (subTexts ?? []).map(s => String(s ?? '')).filter(Boolean);
  return [primaryText, ...subs].join('\n');
};
